<script lang="ts">
  import { isActive, goto } from '@roxi/routify';
  import { onMount } from 'svelte';
  import { locale, t } from 'svelte-i18n';
  import CarbonautLogo from './carbonaut-logo.svelte';
  import {
    checkInitialHeaderColors,
    HeaderPageReference,
    headerStore,
    pages,
    setInitialHeaderColors,
  } from './header-store';
  import LanguageRadio from './language-radio.svelte';
  import { scrollTo } from './utils';

  type Theme = 'dark' | 'light';

  let selectionTheme: Theme = 'light';
  let theme: string;
  let background: string;
  export let header: HTMLElement = undefined;

  let en: boolean, pt: boolean;
  let menuOpened = false;
  let triggerClose = false;

  const body = document.querySelector('body');

  headerStore.subscribe((header) => {
    background = header.background;
    theme = header.theme;

    checkTextSelectionColor();
  });

  onMount(() => {
    subscribeToLanguages();
    checkInitialHeaderColors();
    setCloseMenuListeners();
  });

  function openMenu() {
    menuOpened = true;
    triggerClose = false;
    body.classList.add('menu-opened');
    addBackdrop();
  }

  function setCloseMenuListeners() {
    window.addEventListener('resize', closeMenuOnResize);
    window.addEventListener('popstate', closeMenuImmeadiately);
  }

  function addBackdrop() {
    const backdropElement = document.querySelector('#menu-backdrop');
    if (backdropElement) {
      backdropElement.classList.add('u-backdrop__menu');
    }
  }

  function closeMenu() {
    return new Promise((resolve) => {
      triggerClose = true;
      removeBackdrop();

      setTimeout(() => {
        menuOpened = false;
        body.classList.remove('menu-opened');
        triggerClose = false;
        resolve(true);
      }, 400);
    });
  }

  function closeMenuImmeadiately() {
    removeBackdrop();

    menuOpened = false;
    body.classList.remove('menu-opened');
    triggerClose = false;
  }

  function removeBackdrop() {
    const backdropElement = document.querySelector('#menu-backdrop');
    if (backdropElement) {
      backdropElement.classList.remove('u-backdrop__menu');
    }
  }

  function closeMenuOnResize() {
    if (window.innerWidth > 1024 && menuOpened) {
      closeMenu();
    }
  }

  async function handleItemClick(path: HeaderPageReference) {
    const hasMenu = window.innerWidth < 1024;

    if (hasMenu) {
      await closeMenu();
    }

    navigate(path);
  }

  function checkAfterClickOnLogo() {
    setTimeout(() => {
      scrollTo({ top: 0 });
      checkInitialHeaderColors(true);
    }, 50);
  }

  function navigate(page: HeaderPageReference) {
    $goto(page.path);
    window.scrollTo(0, 0);
    setInitialHeaderColors(page.theme, page.background);
  }

  function checkTextSelectionColor() {
    if (background === 'blue') {
      selectionTheme = 'dark';
      return;
    }

    selectionTheme = 'light';
  }

  function subscribeToLanguages() {
    locale.subscribe((iso) => {
      en = iso.includes('en');
      pt = iso.includes('pt');
    });
  }
</script>

<header class="fixed min-w-full z-50" bind:this={header}>
  <div
    class={`header-inner h-[72px] md2:h-[80px] xl:h-[104px] background-${background} bg-${background} selection-${selectionTheme}`}
  >
    <a
      href="/"
      on:click={checkAfterClickOnLogo}
      class={`header-logo logo-${theme}`}
      draggable="false"
    >
      <CarbonautLogo />
    </a>

    <!-- mobile menu button -->
    <button class="md2:hidden ml-auto" on:click={openMenu}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class={`w-[30px] logo-${theme}`}
        viewBox="0 0 512 512"
      >
        <title>Menu</title>
        <path
          fill="none"
          stroke="currentColor"
          stroke-linecap="round"
          stroke-miterlimit="10"
          stroke-width="32"
          d="M80 160h352M80 256h352M80 352h352"
        />
      </svg>
    </button>
    <div
      class="hidden z-10 md2:items-center md2:flex animate-show  max-h-[72px] md2:max-h-[80px] xl:max-h-[104px]  ml-auto"
      class:max-h-[unset]={menuOpened}
      class:mobile-menu={menuOpened}
      class:animate-hide={triggerClose}
    >
      <!-- mobile close menu button -->
      <button class="md2:hidden absolute top-8 right-8" on:click={closeMenu}>
        <svg xmlns="http://www.w3.org/2000/svg" class="w-[30px]" viewBox="0 0 512 512">
          <title>Close</title>
          <path
            fill="none"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="32"
            d="M368 368L144 144M368 144L144 368"
          />
        </svg>
      </button>

      <nav class="flex" class:mobile-nav={menuOpened}>
        {#each pages as page}
          <a
            class={`theme-${theme} mobile-item cursor-pointer ${
              $isActive(page.path) ? 'opacity-100' : 'opacity-50'
            }`}
            on:click={() => handleItemClick(page)}
          >
            {$t(`NAV.${page.text}`)}
          </a>
        {/each}
      </nav>

      <div class="flex" class:mobile-languages={menuOpened}>
        <LanguageRadio active={en} iso="en" {theme} />
        <LanguageRadio active={pt} iso="pt" {theme} />

        <div class="flex justify-center items-center ml-6 pl-6 border-l-[1px] border-l-border-gray">
          <a href="https://www.linkedin.com/company/carbonaut/" target="_blank">
            <div class={`w-[32px] h-[32px] c-linkedin c-linkedin--${theme} `} class:c-linkedin--menu={menuOpened}></div>
          </a>
        </div>
      </div>
    </div>
  </div>
  <div
    id="menu-backdrop"
    class="fixed top-0 right-0 bottom-0 left-0 pointer-events-none transition-all duration-[600ms]"
  />
</header>

<style global lang="scss">
  .header-inner {
    @apply px-6 xl:px-10;

    @apply text-xxs xs:text-xs sm:text-sm md:text-md md2:text-base xl:text-xs;

    @apply leading-6 xl:leading-5;

    @apply relative flex items-center;
  }

  .header-logo svg {
    height: 31px;

    @media screen and (min-width: 1601px) {
      height: 39px;
    }
  }

  .mobile-menu {
    height: 100vh; /* Fallback for browsers that do not support Custom Properties */
    max-height: calc(var(--vh, 1vh) * 100);

    @apply absolute block w-full inset-0 bg-white;
  }
  .mobile-nav {
    @apply flex-col absolute top-2/4 left-1/2 text-center;
    transform: translate(-50%, -50%);
  }
  .mobile-languages {
    @apply absolute left-1/2 bottom-12;
    transform: translateX(-50%);
  }
  .mobile-item {
    @apply md2:mr-6 hover:opacity-100 mb-8 sm:mb-12 md:mb-16 md2:mb-0;

    @media screen and (max-width: 1023px) {
      color: black !important;
    }

    &:last-of-type {
      @apply mb-0;
    }
  }

  :global {
    .theme-white {
      @apply text-white;
    }

    .theme-black {
      @apply text-black;
    }
    .theme-red {
      &:hover {
        @apply text-red;
      }
    }
    .theme-orange {
      &:hover {
        @apply text-orange;
      }
    }

    .theme-uniapp {
      &:hover {
        @apply text-uniapp-logo;
      }
    }

    .theme-fprj {
      &:hover {
        @apply text-fprj-logo;
      }
    }

    .theme-purple {
      &:hover {
        @apply text-wellabe-primary;
      }
    }
  }

  .logo-red {
    @apply text-red;
  }
  .logo-orange {
    @apply text-orange;
  }
  .logo-uniapp {
    @apply text-uniapp-logo;
  }

  .logo-fprj {
    @apply text-fprj-logo;
  }

  .logo-purple {
    @apply text-wellabe-primary;
  }
  .logo-white {
    @apply text-white;
  }

  .logo-black {
    @apply text-black;
  }

  .background-blue {
    @apply bg-blue-bg;
  }
  .background-gray {
    @apply bg-gray;
  }
  .background-yellow {
    @apply bg-yellow-light;
  }
  .background-uniapp {
    @apply bg-uniapp-bg-light;
  }

  .background-fprj {
    @apply bg-fprj-bg-light;
  }

  .background-wellabe-light {
    @apply bg-wellabe-bg-light;
  }

  .background-wellabe-medium {
    @apply bg-wellabe-bg-medium;
  }

  .background-md-integrations-light {
    @apply bg-md-integrations-bg-medium;
  }

  .c-linkedin{
    background-image: url('../images/linkedin.svg');
    &--white {
      background-image: url('../images/linkedin-dark.svg');
    }

    &--menu {
      background-image: url('../images/linkedin.svg');
    }
}
  .selection-light {
    @apply selection:bg-blue-bg selection:text-white;
  }

  .selection-dark {
    @apply selection:bg-text-dark selection:text-blue;
  }
</style>
